import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Close, Search, Visibility } from '@mui/icons-material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import api from '../../services/api';

interface AuditPerson { id: string; name: string; email?: string; userType?: string }

interface AuditRow {
  _id: string;
  createdAt: string;
  action: string;
  resource: string;
  resourceId?: string;
  resourceLabel?: string;
  resourceIdMeaning: string;
  actor: AuditPerson | null;
  actorLabel: string;
  outcome: 'success' | 'failure';
  reason?: string;
  ip?: string;
  device?: string;
  method?: string;
  path?: string;
  requestId?: string;
  hasChanges: boolean;
}

interface AuditDetail extends AuditRow {
  userAgent?: string;
  metadata: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  changes: Array<{ field?: string; label?: string; key?: string; category?: string; from: unknown; to: unknown }>;
}

interface Filters {
  q: string;
  resource: string;
  action: string;
  outcome: string;
  user: string;
  userId: string;
  resourceId: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { q: '', resource: '', action: '', outcome: '', user: '', userId: '', resourceId: '', from: '', to: '' };

const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function utcOffset(date = new Date()) {
  const mins = -date.getTimezoneOffset();
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/** 09 Oct 2026, 11:42:05 IST — always with seconds and a zone. */
function formatStamp(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZoneName: 'short',
  }).format(d);
}

/** datetime-local value (browser time) → ISO for the API. */
function toIso(local: string) {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const label = (s: string) => s.replace(/[_.]/g, ' ');

function JsonBlock({ value }: { value: unknown }) {
  return (
    <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}

function AuditDetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', id],
    queryFn: async () => (await api.get(`/admin/audit-logs/${id}`)).data.data as AuditDetail,
    enabled: !!id,
  });
  const fields: Array<[string, React.ReactNode]> = data ? [
    ['When', <>{formatStamp(data.createdAt)}<Typography variant="caption" display="block" color="text.secondary">UTC: {new Date(data.createdAt).toISOString()}</Typography></>],
    ['Outcome', <><Chip size="small" color={data.outcome === 'failure' ? 'error' : 'success'} label={data.outcome === 'failure' ? 'Failed' : 'Succeeded'} />{data.reason && <Typography variant="body2" sx={{ mt: 0.5 }}>{data.reason}</Typography>}</>],
    ['Action', data.action],
    ['Resource', data.resource],
    ['Resource ID', <>{data.resourceId || '—'}<Typography variant="caption" display="block" color="text.secondary">{data.resourceIdMeaning}{data.resourceLabel ? ` — ${data.resourceLabel}` : ''}</Typography></>],
    ['Performed by', data.actor ? <>{data.actor.name}<Typography variant="caption" display="block" color="text.secondary">{[data.actor.userType, data.actor.email, `ID ${data.actor.id}`].filter(Boolean).join(' · ')}</Typography></> : data.actorLabel],
    ['IP address', data.ip || '—'],
    ['Device / browser', [data.device, data.userAgent].filter(Boolean).join(' · ') || '—'],
    ['API request', `${data.method || ''} ${data.path || ''}`.trim() || '—'],
    ['Request ID', <>{data.requestId || '—'}<Typography variant="caption" display="block" color="text.secondary">Shared by every audit record written during the same API call</Typography></>],
  ] : [];
  const { method: _m, path: _p, requestId: _r, ...extraMeta } = data?.metadata || {};

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Audit event</Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2, overflow: 'auto' }}>
        {isLoading && <CircularProgress size={24} />}
        {error && <Alert severity="error">Could not load this record</Alert>}
        {data && (
          <Stack spacing={2}>
            <Table size="small">
              <TableBody>
                {fields.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ width: 140, color: 'text.secondary', verticalAlign: 'top' }}>{k}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{v}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.changes?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Changes (old → new)</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Field</TableCell><TableCell>Old</TableCell><TableCell>New</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {data.changes.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.label || c.field || `${c.category}.${c.key}`}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{c.from == null ? '—' : typeof c.from === 'object' ? JSON.stringify(c.from) : String(c.from)}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{c.to == null ? '—' : typeof c.to === 'object' ? JSON.stringify(c.to) : String(c.to)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {Object.keys(extraMeta).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Details</Typography>
                <JsonBlock value={extraMeta} />
              </Box>
            )}
            {data.before && (<Box><Typography variant="subtitle2" gutterBottom>Before</Typography><JsonBlock value={data.before} /></Box>)}
            {data.after && (<Box><Typography variant="subtitle2" gutterBottom>After / request</Typography><JsonBlock value={data.after} /></Box>)}
            <Typography variant="caption" color="text.secondary">
              Passwords, tokens, API keys, secrets and one-time codes are replaced with [redacted] when the event is recorded.
            </Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export default function AuditLogsPage() {
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: facets } = useQuery({
    queryKey: ['audit-log-facets'],
    queryFn: async () => (await api.get('/admin/audit-logs/facets')).data.data as { resources: string[]; actions: string[] },
  });

  const { data, isFetching, error } = useQuery({
    queryKey: ['audit-logs', applied, pagination],
    queryFn: async () => {
      const res = await api.get('/admin/audit-logs', {
        params: {
          ...Object.fromEntries(Object.entries(applied).filter(([k, v]) => v && k !== 'from' && k !== 'to')),
          from: toIso(applied.from),
          to: toIso(applied.to),
          page: pagination.page + 1,
          limit: pagination.pageSize,
        },
      });
      return res.data as { data: AuditRow[]; meta: { total: number; timezone?: string } };
    },
    placeholderData: keepPreviousData,
  });

  const rangeError = draft.from && draft.to && new Date(draft.from) > new Date(draft.to) ? '"From" must be before "To"' : '';
  const userIdError = draft.userId && !/^[a-f0-9]{24}$/i.test(draft.userId.trim()) ? 'A user ID is 24 characters (0-9, a-f)' : '';
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const activeCount = Object.values(applied).filter(Boolean).length;

  const apply = (next: Filters = draft) => {
    if (rangeError || userIdError) return;
    setApplied({ ...next, userId: next.userId.trim(), resourceId: next.resourceId.trim() });
    setPagination((p) => ({ ...p, page: 0 }));
  };
  const clear = () => { setDraft(EMPTY); setApplied(EMPTY); setPagination((p) => ({ ...p, page: 0 })); };
  const quickRange = (hours: number) => {
    const next = { ...draft, from: toLocalInput(new Date(Date.now() - hours * 3600_000)), to: '' };
    setDraft(next);
    apply(next);
  };
  const filterBy = (patch: Partial<Filters>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    apply(next);
  };
  const field = (key: keyof Filters) => ({
    value: draft[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, [key]: e.target.value })),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') apply(); },
    size: 'small' as const,
  });

  const columns: GridColDef<AuditRow>[] = useMemo(() => [
    {
      field: 'createdAt', headerName: 'Timestamp', width: 200, sortable: false,
      renderCell: ({ row }) => <Tooltip title={`UTC: ${new Date(row.createdAt).toISOString()}`}><span>{formatStamp(row.createdAt)}</span></Tooltip>,
    },
    {
      field: 'outcome', headerName: 'Result', width: 110, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.reason || ''}>
          <Chip size="small" color={row.outcome === 'failure' ? 'error' : 'success'} variant="outlined" label={row.outcome === 'failure' ? 'Failed' : 'Success'} />
        </Tooltip>
      ),
    },
    { field: 'action', headerName: 'Action', width: 170, sortable: false, renderCell: ({ row }) => <Tooltip title={row.reason || row.action}><span>{label(row.action)}</span></Tooltip> },
    { field: 'resource', headerName: 'Resource', width: 130, sortable: false },
    {
      field: 'resourceId', headerName: 'Resource ID', width: 220, sortable: false,
      renderCell: ({ row }) => row.resourceId ? (
        <Tooltip title={`${row.resourceIdMeaning}${row.resourceLabel ? ` — ${row.resourceLabel}` : ''}. Click to show only this record.`}>
          <Box sx={{ cursor: 'pointer', lineHeight: 1.3, overflow: 'hidden' }} onClick={() => filterBy({ resourceId: row.resourceId! })}>
            <Typography variant="body2" noWrap>{row.resourceLabel || row.resourceId}</Typography>
            {row.resourceLabel && <Typography variant="caption" color="text.secondary" noWrap display="block">{row.resourceId}</Typography>}
          </Box>
        </Tooltip>
      ) : '—',
    },
    {
      field: 'actor', headerName: 'User', flex: 1, minWidth: 180, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, overflow: 'hidden' }}>
          <Typography variant="body2" noWrap>{row.actorLabel}</Typography>
          {row.actor?.userType && <Typography variant="caption" color="text.secondary" noWrap display="block">{label(row.actor.userType)}</Typography>}
        </Box>
      ),
    },
    { field: 'ip', headerName: 'IP', width: 130, sortable: false },
    {
      field: 'path', headerName: 'API request', width: 260, sortable: false,
      renderCell: ({ row }) => {
        const text = `${row.method || ''} ${row.path || ''}`.trim();
        return text ? <Tooltip title={text}><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.3 }}>{text}</Typography></Tooltip> : '—';
      },
    },
    {
      field: 'view', headerName: '', width: 70, sortable: false, filterable: false,
      renderCell: ({ row }) => <Tooltip title="View full event"><IconButton size="small" onClick={() => setOpenId(row._id)}><Visibility fontSize="small" /></IconButton></Tooltip>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [draft]);

  const tenantZone = data?.meta?.timezone;
  const apiError = (error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Audit Logs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign-ins (successful and failed), settings, gateway, role, user and template changes for this tenant only.
        Times are shown in your browser time zone, {browserZone} ({utcOffset()}){tenantZone && tenantZone !== browserZone ? ` — the tenant's time zone is ${tenantZone}` : ''}; hover a time for UTC.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
          <TextField label="Search" placeholder="Action, path, IP, reason…" {...field('q')} />
          <TextField select label="Resource" {...field('resource')}>
            <MenuItem value="">All resources</MenuItem>
            {(facets?.resources || []).map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField select label="Action" {...field('action')}>
            <MenuItem value="">All actions</MenuItem>
            {(facets?.actions || []).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select label="Result" {...field('outcome')}>
            <MenuItem value="">Success and failed</MenuItem>
            <MenuItem value="success">Success only</MenuItem>
            <MenuItem value="failure">Failed only</MenuItem>
          </TextField>
          <TextField label="User (name or email)" {...field('user')} />
          <TextField label="User ID" {...field('userId')} error={!!userIdError} helperText={userIdError} />
          <TextField label="Resource ID" placeholder="Full or partial ID" {...field('resourceId')} />
          <Box />
          <TextField label={`From (${browserZone})`} type="datetime-local" InputLabelProps={{ shrink: true }} {...field('from')} error={!!rangeError} helperText={rangeError} />
          <TextField label={`To (${browserZone})`} type="datetime-local" InputLabelProps={{ shrink: true }} {...field('to')} error={!!rangeError} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ gridColumn: { md: 'span 2' }, flexWrap: 'wrap' }}>
            {[['Last 24 h', 24], ['Last 7 days', 168], ['Last 30 days', 720]].map(([l, h]) => (
              <Chip key={l} label={l} size="small" variant="outlined" onClick={() => quickRange(Number(h))} />
            ))}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
          <Button variant="contained" startIcon={<Search />} onClick={() => apply()} disabled={!!rangeError || !!userIdError}>
            {dirty ? 'Apply filters' : 'Search'}
          </Button>
          <Button onClick={clear} disabled={!activeCount && !dirty}>Clear</Button>
          <Typography variant="caption" color="text.secondary">
            {dirty ? 'Filters changed — press Apply (or Enter) to update the list' : activeCount ? `${activeCount} filter(s) applied` : 'No filters applied'}
            {data ? ` · ${data.meta.total} record(s)` : ''}
          </Typography>
        </Stack>
      </Paper>

      {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
      {applied.resourceId && data && !data.meta.total && (
        <Alert severity="info" sx={{ mb: 2 }}>No audit records for resource ID containing &ldquo;{applied.resourceId}&rdquo; in this tenant.</Alert>
      )}

      <SerialNoDataGrid
        rows={data?.data || []}
        columns={columns}
        loading={isFetching}
        getRowId={(r) => r._id}
        autoHeight
        getRowHeight={() => 'auto'}
        paginationMode="server"
        rowCount={data?.meta.total ?? 0}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[25, 50, 100]}
        onRowDoubleClick={({ row }) => setOpenId(row._id)}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, '& .MuiDataGrid-cell': { py: 1, display: 'flex', alignItems: 'center' } }}
      />

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>What is recorded and hidden:</strong> passwords, tokens, API keys, secrets and one-time codes are redacted when an event is written.
        Email addresses and phone numbers are masked here while Partner Onboarding &rarr; &ldquo;Mask Email &amp; Mobile&rdquo; is on.
        IP address, device and API path are kept because they are needed to investigate sign-ins and changes.
        For sign-in events the Resource ID is the account that signed in (or tried to); failed attempts for unknown accounts show the masked email/phone that was entered.
      </Alert>

      <AuditDetailDrawer id={openId} onClose={() => setOpenId(null)} />
    </Box>
  );
}
